import Cookies from "universal-cookie"
import * as T from "../utilities/frontendTypes";
import { FieldErrors, FieldValues } from "react-hook-form";
import { Address } from "../Components/Form/AddressFields";
import { ListRowContentValue } from "../Components/List";
import { Location } from "react-router-dom";
import { ToastInfo, ToastOption } from "./customHooks";
import uniqid from 'uniqid';

export * from "./taxUtils";
export * from "./formUtilities";

export async function getAuthHeader() {
  const cookie = new Cookies();
  const token = cookie.get("authToken");

  if (!token) {
    throw new Error("No auth token available");
  }

  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`
  };
}

export async function getUploadAuthHeader() {
  const cookie = new Cookies();
  const token = cookie.get("authToken");

  if (!token) {
    throw new Error("No auth token available");
  }

  return {
    'Content-Type': 'multipart/form-data',
    Authorization: `Bearer ${token}`
  };
}

/**
 * Ensures a URL has an https:// prefix. If the URL already has a protocol (http:// or https://),
 * it returns the URL unchanged. Otherwise, it prepends https://.
 */
export const ensureHttps = (url: string | undefined): string => {
  if (!url) return '';
  return url.match(/^https?:\/\//i) ? url : `https://${url}`;
};

export const getRouteNameFromUserRole = (userRole: T.UserRole) => {
  switch (userRole) {
    case "partnerAdmin":
    case "superAdmin":
      return "admins";
    case "serviceProvider":
      return "providers";
    case "startupFounder":
      return "founders";
    case "ipExpert":
      return "ipExpert";
    default:
      throw new Error(`Unknown user role: ${userRole}`);
  }
}

/*
  Function: isFormInvalid

  Purpose: check if form is valid or not

  Return: boolean
*/
export const isFormInvalid = (errors: FieldErrors<FieldValues>) => {
  return Object.keys(errors).length > 0;
}

/*
  Function: checkInputErrorByKey

  Purpose: check if the error object contains errors that match to a given field name (fieldKey)

  Return: A filtered error object
*/
export const checkInputErrorByKey = (errors: FieldErrors<FieldValues>, fieldKey: string) => {
  const filteredErrors: FieldErrors<FieldValues> = Object.keys(errors) 
    .filter(key => key.includes(fieldKey)) // filter out errors that belong to the fieldKey
    .reduce((curr, key) => {
      return Object.assign(curr, { error: errors[key]}) // map errors
    }, {});

  return filteredErrors;
}

/*
  Function: flattenObject

  Purpose: Flatten a multidimensional object
 
  For example:
    flattenObject{ a: 1, b: { c: 2 } }
  Returns:
    { a: 1, c: 2}
 */

export const flattenObject = <T>(obj: Record<string, any>) => {
  const flattened: Record<string, any> = {};

  Object.keys(obj).forEach((key) => {
    const value = obj[key];

    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      Object.assign(flattened, flattenObject(value as Record<string, any>));
    } else {
      flattened[key] = value;
    }
  });

  return flattened;
};

/*
  Function: findNestedValueByKey

  Purpose: find object in a nested object (including arrays) by given key-value pair

  Return: result object
*/
export const findNestedValueByKey = (obj: Object, key: string) => {
  try {
    JSON.stringify(obj, (_, nestedValue) => {
      if (nestedValue && nestedValue[key]) throw nestedValue[key]
      return nestedValue;
    })
  } catch (result) {
    return result;
  }
}

// [TODO] add doc
export const addressToString = (address: Address): string => {
  
  const { streetNumber, streetName, unitNumber, city, province, country, postalCode} = address;

  const addressString = streetName !== "" ? `${streetNumber} ${streetName}${(unitNumber && unitNumber !== "") ? ` #${unitNumber}` : ""}, ${city}, ${province}, ${country}, ${postalCode}` : "";

  return addressString;
}

// [TODO] add doc
export type KeyValuePair = {
  [key: string]: ListRowContentValue;
}

// export const sortObjectsByKey = (
//   contentArray: KeyValuePair[], 
//   key: string, 
//   ascending?: boolean, 
//   options?: { sortOptions: ListRowContentValue[], currentOptionIndex: number }
// )  => {
//   if (options) {
//     const keyValue = options.sortOptions[options.currentOptionIndex];
//     return contentArray
//     .slice()
//     .sort((a: T.listComparison, b: T.listComparison) => {
//       if (a[key] === keyValue && b[key] !== keyValue) {
//         return -1;
//       } else if (a[key]!== keyValue && b[key] === keyValue) {
//         return 1;
//       } 
//       return 0;
//     });
//   } else {
//     return contentArray
//     .slice()
//     .sort((a: T.listComparison, b: T.listComparison) => {
//       const valueA = a[key];
//       const valueB = b[key];
//         if (valueA < valueB) {
//           return ascending ? -1 : 1;
//         } else if (valueA > valueB) {
//           return ascending ? 1 : -1;
//         }
//       return 0;
//     });
//   }
// };

export const sortObjectsByKey = (
  contentArray: KeyValuePair[], 
  key: string, 
  ascending?: boolean, 
  options?: { sortOptions: ListRowContentValue[], currentOptionIndex: number }
)  => {
  if (options) {
    const keyValue = options.sortOptions[options.currentOptionIndex];
    let sortKey = key
    return contentArray
    .slice()
    .sort((a: KeyValuePair, b: KeyValuePair) => {
      if (a[sortKey] === keyValue && b[sortKey] !== keyValue) {
        return -1;
      } else if (a[sortKey] !== keyValue && b[sortKey] === keyValue) {
        return 1;
      } 
      return 0;
    });

  } else {
    return contentArray
    .slice()
    .sort((a: KeyValuePair, b: KeyValuePair) => {
      const valueA = a[key];
      const valueB = b[key];

      // Handle Date objects
      if (valueA instanceof Date && valueB instanceof Date) {
        const timeA = valueA.getTime();
        const timeB = valueB.getTime();
        return ascending ? timeA - timeB : timeB - timeA;
      }

      // Handle ISO date strings (e.g., "2026-02-09T18:52:58.000Z")
      const dateA = new Date(valueA);
      const dateB = new Date(valueB);
      if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
        return ascending ? dateA.getTime() - dateB.getTime() : dateB.getTime() - dateA.getTime();
      }

      // String comparison for other types
      const strA = valueA.toString().toLowerCase();
      const strB = valueB.toString().toLowerCase();
      return ascending ? strA.localeCompare(strB) : strB.localeCompare(strA);
    });
  }
};

export const isStringNumeric = (str: string) => {
  return !isNaN(+str)
}

export const isObjectEmpty = (obj: Object) => {
  for (const prop in obj) {
    if (Object.hasOwn(obj, prop)) {
      return false;
    }
  }

  return true;
}

export const isFromUrls = (specificUrls: string[], location: Location) => {
  const from = location.state?.from || "/";

  return specificUrls.some(url => from.includes(url));
}


/*
  Function: handleFormSaveResponse

  Purpose:

  Return: 
*/
export const handleFormSaveResponse = (res: any, showToast: (toastOption: ToastOption) => void, successToastInfo?: ToastInfo, failureToastInfo?: ToastInfo ) => { // the 'any' here is intentional and safe

  if (typeof res.success !== "boolean") {
    throw new Error (`unknown res type: ${res}`);
  }
  if (res.success === true) {
    showToast({
      toastType: "success",
      ...successToastInfo,
    });
  } else {
    showToast({
      toastType: "failure",
      ...failureToastInfo,
    });
  }
}

export function generateId() {
  return uniqid();
};

/*
  Function: getRegionNameFromRegion

  Purpose:

  Return: 
*/
export const getRegionNameFromRegion = (region: T.UserRegion) => {

  switch (region) {
    case "communitech":
      return "Communitech";
    case "investOttawa":
      return "Invest Ottawa";
    case "northForge":
      return "North Forge";
    default:
      throw new Error(`Unknown region: ${region}`);
  }
}

/*
  Function: getIsOSDarkMode

  Purpose: detect whether the operation system is in dark mode

  Return: Boolean, true if dark mode
*/
export const getIsOSDarkMode = () => {
  return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
}


export const getUserLanguage = () => {
  return navigator.language;
}

/*
  Function: getLastPartOfPath

  Purpose: return last part of a path of a given path string

  Return: string
*/
export const getLastPartOfPath = (path: string) => {
  return path.slice(path.lastIndexOf("/") + 1, path.length);
}

/*
  Function: getDocumentDisplayName

  Purpose: turn a snapshot document key/path pair into a human-friendly label.
  The legacy schema stores raw S3 filenames as values (e.g. "<id>-pitch-deck-<ts>.pdf"),
  so we detect the document type from either the schema key (e.g. "pitchdeck") or
  filename substrings, and fall back to a cleaned filename if nothing matches.
*/
export const getDocumentDisplayName = (key: string, path: string, companyName?: string): string => {
  const company = companyName || "Company";
  const k = key.toLowerCase();
  const p = (path || "").toLowerCase();

  if (k === "pitchdeck" || p.includes("pitch-deck") || p.includes("pitchdeck")) {
    return `${company} Pitch Deck`;
  }
  if (k === "nda" || p.includes("nda")) {
    return `${company} NDA`;
  }
  // Fallback: filename basename, or the schema key if path is empty
  return getLastPartOfPath(path) || key;
}

/*
  Function: checkIsOnline

  Purpose: return true/false value indicating online status

  Return: boolean
*/
export const checkIsOnline = async (): Promise<boolean> => {
  try {
    if (!self.navigator.onLine) return false; // false check is reliable
    const request = new URL(self.location.origin); // same origin request to avoid CROS error
    request.searchParams.set('rand', Date.now().toString()); // random value to prevent cached responses
    const response = await fetch(request.toString(), { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
}

export const daysAgo = (date: Date) => {
    const currentDate = new Date(); // Get the current date
    const pastDate = new Date(date); // Convert the input to a Date object
    const differenceInTime = currentDate.getTime() - pastDate.getTime(); // Difference in milliseconds
    const differenceInDays = Math.floor(differenceInTime / (1000 * 60 * 60 * 24)); // Convert milliseconds to days
    return differenceInDays;
  }

export function formatOrdinalDate(date: Date): string {
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const month = monthNames[date.getMonth()];
  const day = date.getDate();
  let suffix = "th";
  if (day % 10 === 1 && day !== 11) suffix = "st";
  else if (day % 10 === 2 && day !== 12) suffix = "nd";
  else if (day % 10 === 3 && day !== 13) suffix = "rd";
  return `${month} ${day}${suffix}`;
}


/** Display label for a manual DD category value (ddObj.cat1..cat6). */
export function ddToString(value: string): string {
  switch (value) {
    case "fail":
      return "Fail";
    case "pass":
      return "Pass";
    case "conditionalPass":
      return "Conditional Pass (followup done by Growth Coach)";
    case "issue":
      return "Ongoing Due Diligence";
    default:
      return "No Selection Made";
  }
}

/**
 * Splits legacy packed contact-email values ("a@x.com; b@y.com") into
 * individual addresses. Handles the Mixed-typed firm contactEmail field
 * (string, packed string, or string[]).
 * NEVER use for contact names — legit commas exist ("Cory J. Furman, K.C.").
 */
export function splitContactEmails(value: string | string[] | undefined | null): string[] {
  const parts = Array.isArray(value) ? value : typeof value === "string" ? value.split(/[;,]/) : [];
  return parts.map((p) => (p ?? "").trim()).filter(Boolean);
}

/** Display form for an editable contact field: arrays joined with ", ", strings as-is. */
export function joinContactValues(value: string | string[] | undefined | null): string {
  return Array.isArray(value) ? value.filter(Boolean).join(", ") : (value ?? "");
}

/**
 * Parses a human-entered email list (textarea: one per line, or comma/
 * semicolon-separated) into a normalized (trim+lowercase), deduped array.
 */
export function parseEmailList(raw: string): string[] {
  const out: string[] = [];
  for (const part of raw.split(/[\n,;]/)) {
    const email = part.trim().toLowerCase();
    if (email && !out.includes(email)) out.push(email);
  }
  return out;
}

/**
 * True when a final-invoice deadline has already passed — the deadline DAY is
 * fully over, so a deadline falling today is not yet overdue. Invalid or
 * missing values are never overdue.
 */
export function isDeadlineOverdue(value: string | Date | null | undefined): boolean {
  if (!value) return false;
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) return false;
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  return date.getTime() < startOfToday.getTime();
}
